import { type ReactElement, useEffect } from "react"
import { Stack } from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { ErrorMessageAlert } from "@/components/molecules/ErrorMessageAlert"
import { Actions } from "@/components/organisms"
import { InvoiceUploadCard } from "@/features/invoiceUpload/components/InvoiceUploadCard"
import { onboardingDataLayer, OnboardingDataLayerEvent } from "@/features/onboarding/datalayer"
import { onboardingWizard, useOnboardingToStep } from "@/features/onboarding/wizard"
import { SubscriptionContainer } from "@/features/subscription"
import { OnboardingRouteNames } from "@/routes/onboarding"
import {
    AddressLookupForm,
    FunnelTypeToggle,
    InitialOnboardingFormProvider,
    InputMethodToggle,
    JourneyTypeToggle,
    MeterPointEligibility,
    useInitialOnboardingFormContext,
    useValidateInvoiceUpload,
} from "../meterEligibility"
import { OnboardingLayout } from "./OnboardingLayout"

type OnboardingMeterDetailsPageProps =
    | {
          errorCode: string | null
          serializedState: ReturnType<typeof onboardingWizard.serializeState>
      }
    | {
          errorMessage: string
          serializedState: ReturnType<typeof onboardingWizard.serializeState>
      }

type OnboardingMeterDetailsPageContentProps = Exclude<
    OnboardingMeterDetailsPageProps,
    { errorMessage: string }
>

function OnboardingMeterDetailsPageContent(props: OnboardingMeterDetailsPageContentProps) {
    const t = useTranslations("OnboardingPage")

    const { toPreviousStep } = useOnboardingToStep(OnboardingRouteNames.MeterDetails)
    const onboardingState = onboardingWizard.deserializeState(props.serializedState)
    const { familyName, givenName, inputMethod } = useInitialOnboardingFormContext()

    const validateInvoiceUpload = useValidateInvoiceUpload()

    useEffect(() => {
        onboardingDataLayer.sendTrackingEvent(
            OnboardingDataLayerEvent.FunnelOpen,
            onboardingState.formValues
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Stack
            flexDirection="column"
            gap="lg"
        >
            <SubscriptionContainer.Section>
                <SubscriptionContainer.StepTitle>
                    {t("title", { subscriptionType: onboardingState.formValues.subscriptionType })}
                </SubscriptionContainer.StepTitle>
                <JourneyTypeToggle />
                <FunnelTypeToggle />
                <InputMethodToggle />
            </SubscriptionContainer.Section>

            {inputMethod === "addressLookup" && <AddressLookupForm />}
            {inputMethod === "invoice" && (
                <InvoiceUploadCard
                    givenName={givenName}
                    familyName={familyName}
                    validateInvoiceUpload={validateInvoiceUpload.submit}
                    validateInvoiceUploadError={validateInvoiceUpload.error}
                />
            )}
            {inputMethod === "meterPoint" && <MeterPointEligibility />}

            {onboardingState.formValues.subscriptionType === "MOVE_OUT" && (
                <Actions step>
                    <Actions.Cancel onClick={() => toPreviousStep(onboardingState)} />
                </Actions>
            )}
        </Stack>
    )
}

export function OnboardingMeterDetailsPage(props: OnboardingMeterDetailsPageProps) {
    if ("errorMessage" in props) {
        return <ErrorMessageAlert errorMessage={props.errorMessage} />
    }

    return <OnboardingMeterDetailsPageContent {...props} />
}

OnboardingMeterDetailsPage.getLayout = (page: ReactElement<OnboardingMeterDetailsPageProps>) => {
    const onboardingState = onboardingWizard.deserializeState(page.props.serializedState)

    return (
        <InitialOnboardingFormProvider onboardingState={onboardingState}>
            <OnboardingLayout
                serializedState={page.props.serializedState}
                stepName={OnboardingRouteNames.MeterDetails}
            >
                {page}
            </OnboardingLayout>
        </InitialOnboardingFormProvider>
    )
}

OnboardingMeterDetailsPage.messages = [
    "OnboardingPage",
    ...AddressLookupForm.messages,
    ...ErrorMessageAlert.messages,
    ...FunnelTypeToggle.messages,
    ...InputMethodToggle.messages,
    ...InvoiceUploadCard.messages,
    ...JourneyTypeToggle.messages,
    ...MeterPointEligibility.messages,
    ...OnboardingLayout.messages,
] as const
